import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { List, X, SignOut } from "@phosphor-icons/react";
import { useState } from "react";

const NAV = [
  { to: "/chat", label: "Chat" },
  { to: "/price-check", label: "Price Check" },
  { to: "/verify", label: "Verify" },
  { to: "/style-match", label: "Style Match" },
  { to: "/resell", label: "Resell" },
  { to: "/blog", label: "Journal" },
  { to: "/pricing", label: "Pricing" },
];

export const Logo = ({ size = "md" }) => {
  const h = size === "sm" ? "h-6" : size === "lg" ? "h-12" : "h-9";
  return (
    <div className="flex items-center gap-2" data-testid="brand-logo">
      <img
        src="/mineai-logo.jpeg"
        alt="Mine AI"
        className={`${h} w-auto select-none`}
        draggable="false"
      />
    </div>
  );
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const companyName = settings?.company_name || "Auromine Retail POS Pvt Ltd";
  const companyAddress = settings?.company_address || "Chennai, Tamil Nadu, India";
  const generalEmail = settings?.general_email || "hello@mineai.io";
  const supportEmail = settings?.support_email || "support@mineai.io";

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className="mc-glass fixed top-0 left-0 right-0 z-50"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="logo-link">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide smooth ${
                    isActive
                      ? "text-[#3B5BDB] font-medium"
                      : "text-[#0A1128]/80 hover:text-[#3B5BDB]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {user ? (
              <div className="flex items-center gap-3">
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin"
                    className="text-sm text-[#3B5BDB] hover:text-[#2F49AF] smooth font-medium"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className="text-sm text-[#0A1128]/80 hover:text-[#3B5BDB] smooth"
                >
                  Dashboard
                </Link>
                <button
                  data-testid="logout-btn"
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  className="text-sm text-[#0A1128]/60 hover:text-[#DC2626] smooth flex items-center gap-1"
                >
                  <SignOut size={16} weight="duotone" /> Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                className="px-5 py-2 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth"
              >
                Sign in
              </Link>
            )}
          </nav>

          <button
            className="lg:hidden text-[#0A1128]"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
            aria-label="menu"
          >
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>

        {open && (
          <div
            className="lg:hidden border-t border-[#E8DFD1] bg-[#FDFAF5]"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="text-base text-[#0A1128]/80"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(" ", "-")}`}
                >
                  {n.label}
                </Link>
              ))}
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setOpen(false)}
                    className="text-base text-[#0A1128]/80"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      navigate("/");
                      setOpen(false);
                    }}
                    className="text-left text-base text-[#DC2626]"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="px-5 py-2 rounded-md bg-[#3B5BDB] text-white text-sm font-medium text-center"
                >
                  Sign in
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pt-16">{children}</main>

      <footer
        className="border-t border-[#E8DFD1] bg-[#F5EFE6]/40 mt-20 print:hidden"
        data-testid="site-footer"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
            <div className="col-span-2 md:col-span-1">
              <Logo />
              <p className="mt-3 text-sm text-[#4A5568] max-w-xs leading-relaxed">
                Your AI jewellery expert. Buy smart, save more, choose with confidence.
              </p>
              <p className="mt-4 text-xs text-[#4A5568]/80 leading-relaxed" data-testid="footer-company">
                {companyName}
                <br />
                {companyAddress}
              </p>
            </div>

            <FooterCol
              title="Product"
              links={[
                { to: "/chat", label: "Chat" },
                { to: "/price-check", label: "Price Check" },
                { to: "/verify", label: "Hallmark Verify" },
                { to: "/style-match", label: "Style Match" },
                { to: "/resell", label: "Resell Valuator" },
              ]}
            />
            <FooterCol
              title="Company"
              links={[
                { to: "/blog", label: "Journal" },
                { to: "/pricing", label: "Pricing" },
                { to: "/press", label: "Press & resources" },
                { href: `mailto:${generalEmail}`, label: "Contact" },
                { href: `mailto:${supportEmail}`, label: "Support" },
                { href: `mailto:${generalEmail}?subject=Mine AI for retailers`, label: "For retailers" },
              ]}
            />
            <FooterCol
              title="Legal"
              links={[
                { to: "/privacy", label: "Privacy Policy" },
                { to: "/terms", label: "Terms & Conditions" },
                { to: "/ai-policy", label: "AI Policy" },
                { to: "/refund-policy", label: "Refund Policy" },
              ]}
            />
          </div>

          <div className="mt-10 pt-6 border-t border-[#E8DFD1] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[#4A5568]/80">
            <span>
              &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
            </span>
            <span className="flex items-center gap-3">
              <a href={`mailto:${generalEmail}`} className="hover:text-[#3B5BDB] smooth" data-testid="footer-general-email">
                {generalEmail}
              </a>
              <span className="opacity-40">·</span>
              <span>mineai.io</span>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#3B5BDB] font-semibold">
        {title}
      </p>
      <ul className="mt-4 space-y-2">
        {links.map((l) => (
          <li key={l.label}>
            {l.href ? (
              <a
                href={l.href}
                className="text-sm text-[#0A1128]/80 hover:text-[#3B5BDB] smooth"
              >
                {l.label}
              </a>
            ) : (
              <Link
                to={l.to}
                className="text-sm text-[#0A1128]/80 hover:text-[#3B5BDB] smooth"
                data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
